import { JanusAPIv2Client } from '../client';
import { 
    Options,
    DataUploadOptions,
    BaseRequestOptions,
    JanusApiError, 
    DataItem, 
    QueryDataItemsDto, 
    CreateDataItemDto, 
    UpdateDataItemDto, 
    DataItemMetadata,
    DataQueryResponse,
    PaginationInfo
} from '../types';
// Conditionally import Node.js specific modules
// import type FormDataLib from 'form-data';
// import type { Readable } from 'stream';
// Handle different data types for cross-platform compatibility
import { cleanUndefined } from '../utils';

// Interface for file upload options
export interface FileUploadOptions {
  /** Optional metadata to attach to the file */
  metadata?: Record<string, any>;
  /** Optional description for the file */
  description?: string;
  /** Optional content type. If not provided, will be inferred from the file */
  contentType?: string;
}

// Interface for simplified upload result
export interface UploadResult {
  /** The key where the file was stored */
  key: string;
  /** The size of the uploaded file in bytes */
  size: number;
  /** Whether the upload was successful */
  success: boolean;
}

// Interface for query options that doesn't expose HTTP details
export interface DataQueryOptions {
  /** Filter by path prefix within the zone */
  pathPrefix?: string;
  /** Maximum number of items per page */
  limit?: number;
  /** Page number (1-indexed) */
  page?: number;
  /** Field to sort by */
  sortBy?: string;
  /** Sort direction */
  sortOrder?: 'ASC' | 'DESC';
  /** Filter by metadata fields */
  metadataFilter?: Record<string, any>;
}

export class DataClient {
  private client: JanusAPIv2Client;

  constructor(client: JanusAPIv2Client) {
    this.client = client;
  }

  // Helper to encode path components safely
  private encodePath(path: string): string {
    return path.split('/').map(component => encodeURIComponent(String(component))).join('/');
  }

  // Helper to convert our clean query options to the internal DTO
  private convertQueryOptions(options: DataQueryOptions): QueryDataItemsDto {
    return {
      pathPrefix: options.pathPrefix,
      limit: options.limit,
      page: options.page,
      sortBy: options.sortBy,
      sortOrder: options.sortOrder,
      metadataQuery: options.metadataFilter
    };
  }

  /**
   * Upload a file to the specified zone and path.
   * This method handles the complexity of the two-step upload process internally.
   * @param zone The data zone where the file should be stored.
   * @param path The path where the file should be stored within the zone.
   * @param file The file content as a Blob, File, or Buffer object.
   * @param options Optional upload configuration.
   * @returns A promise resolving to the upload result.
   */
  async uploadFile(
    zone: string,
    path: string,
    file: Blob | File | any,
    options: FileUploadOptions = {}
  ): Promise<UploadResult> {
    if (!zone || !path) {
      throw new Error('Zone and path are required for file upload');
    }
    if (!file) {
      throw new Error('File content is required');
    }

    try {
      // Determine content type
      let contentType = options.contentType;
      if (!contentType) {
        if (file instanceof File) {
          contentType = file.type || 'application/octet-stream';
        } else if (file instanceof Blob) {
          contentType = file.type || 'application/octet-stream';
        } else {
          contentType = 'application/octet-stream';
        }
      }

      // Step 1: Create metadata entry with placeholder content (like storeJson does)
      const metadata = {
        ...options.metadata,
        contentType: contentType
      };

      // Follow the same pattern as storeJson - include content field
      const data: Partial<CreateDataItemDto> = {
        content: null, // Placeholder content, will be replaced by blob upload
        metadata: metadata,
        description: options.description
      };

      await this.upsertData(zone, path, data);

      // Step 2: Upload the actual blob content
      const uploadResult = await this.uploadBlob(zone, path, file, contentType);

      // Step 3: Return the result in the expected format
      return {
        key: uploadResult.key,
        size: uploadResult.originalSize,
        success: true
      };

    } catch (error) {
      // Provide better error messages that don't expose HTTP details
      if (error instanceof JanusApiError) {
        if (error.status === 404) {
          throw new Error(`Upload failed: Zone '${zone}' not found or not accessible`);
        } else if (error.status === 403) {
          throw new Error(`Upload failed: Permission denied for zone '${zone}'`);
        } else if (error.status === 400) {
          throw new Error(`Upload failed: Invalid file or path. Please check your file and try again.`);
        }
      }
      throw new Error(`File upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Query data items within a zone using simplified options.
   * @param zone The data zone to query.
   * @param options Optional query configuration.
   * @returns A promise resolving to the query results.
   */
  async queryData(
    zone: string,
    options: DataQueryOptions = {}
  ): Promise<DataQueryResponse> {
    if (!zone) throw new Error('Zone cannot be empty');
    const path = `data/${encodeURIComponent(zone)}/query`;
    const queryDto = this.convertQueryOptions(options);
    const cleanedQuery = cleanUndefined(queryDto);
    return this.client._request<DataQueryResponse>('GET', path, { params: cleanedQuery as any });
  }

  /**
   * Get metadata for a specific data item by path.
   * @param zone The data zone.
   * @param itemPath The path to the data item within the zone.
   * @returns A promise resolving to the DataItemMetadata.
   */
  async getMetadata(
    zone: string,
    itemPath: string
  ): Promise<DataItemMetadata> {
    if (!zone || !itemPath) throw new Error('Zone and itemPath cannot be empty');
    const path = `data/${encodeURIComponent(zone)}/metadata/${this.encodePath(itemPath)}`;
    return this.client._request('GET', path);
  }

  /**
   * Get metadata for a specific data item by UUID.
   * @param zone The data zone.
   * @param uuid The UUID of the data item.
   * @returns A promise resolving to the DataItemMetadata.
   */
  async getMetadataByUuid(
    zone: string,
    uuid: string
  ): Promise<DataItemMetadata> {
    if (!zone || !uuid) throw new Error('Zone and uuid cannot be empty');
    const path = `data/${encodeURIComponent(zone)}/by-uuid/${encodeURIComponent(uuid)}`;
    return this.client._request('GET', path);
  }

  /**
   * Get the full data item (content and metadata) by path.
   * For large binary files, consider using getFile instead.
   * @param zone The data zone.
   * @param itemPath The path to the data item within the zone.
   * @returns A promise resolving to the DataItem.
   */
  async getData<T = any>(
    zone: string,
    itemPath: string
  ): Promise<DataItem<T>> {
    if (!zone || !itemPath) throw new Error('Zone and itemPath cannot be empty');
    const path = `data/${encodeURIComponent(zone)}/${this.encodePath(itemPath)}`;
    return this.client._request<DataItem<T>>('GET', path);
  }

  /**
   * Get JSON content from a data item. Throws an error if the content is not valid JSON.
   * @param zone The data zone.
   * @param itemPath The path to the data item within the zone.
   * @returns A promise resolving to the parsed JSON content.
   */
  async getJsonData<T = any>(
    zone: string,
    itemPath: string
  ): Promise<T> {
    const item = await this.getData<T>(zone, itemPath);
    
    if (item.content === null || item.content === undefined) {
      throw new Error(`No content available for item at ${zone}/${itemPath}. Use getRawBlob for binary content.`);
    }
    
    // If content is already parsed JSON, return it
    if (typeof item.content === 'object') {
      return item.content as T;
    }
    
    // If content is a string, try to parse it as JSON
    if (typeof item.content === 'string') {
      try {
        return JSON.parse(item.content) as T;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        throw new Error(`Content at ${zone}/${itemPath} is not valid JSON: ${errorMessage}`);
      }
    }
    
    throw new Error(`Unexpected content type for ${zone}/${itemPath}: ${typeof item.content}`);
  }

  /**
   * Get text content from a data item. Returns the content as a string.
   * @param zone The data zone.
   * @param itemPath The path to the data item within the zone.
   * @returns A promise resolving to the text content.
   */
  async getTextData(
    zone: string,
    itemPath: string
  ): Promise<string> {
    const item = await this.getData<string>(zone, itemPath);
    
    if (item.content === null || item.content === undefined) {
      throw new Error(`No content available for item at ${zone}/${itemPath}. Use getRawBlob for binary content.`);
    }
    
    // If content is already a string, return it
    if (typeof item.content === 'string') {
      return item.content;
    }
    
    // If content is an object (parsed JSON), stringify it
    if (typeof item.content === 'object') {
      return JSON.stringify(item.content);
    }
    
    // For other types, convert to string
    return String(item.content);
  }

  /**
   * Get content from a data item with automatic type detection and conversion.
   * Returns the content in the most appropriate format based on the content type.
   * @param zone The data zone.
   * @param itemPath The path to the data item within the zone.
   * @returns A promise resolving to the content in the appropriate format.
   */
  async getContentAuto<T = any>(
    zone: string,
    itemPath: string
  ): Promise<{ content: T | string | null; contentType: string; metadata: any }> {
    const item = await this.getData<T>(zone, itemPath);
    
    return {
      content: item.content,
      contentType: item.contentType || 'application/octet-stream',
      metadata: item
    };
  }

  /**
   * Get a file as a Blob from the data store.
   * This is a convenience method that calls getRawBlob internally.
   * @param zone The data zone.
   * @param itemPath The path to the file within the zone.
   * @returns Promise resolving to the file as a Blob.
   */
  async getFile(
    zone: string,
    itemPath: string
  ): Promise<Blob> {
    return this.getRawBlob(zone, itemPath);
  }

  /**
   * Get the raw blob content of a data item.
   * @param zone The data zone.
   * @param itemPath The path to the data item within the zone.
   * @returns Promise resolving to the raw blob content.
   */
  async getRawBlob(
    zone: string,
    itemPath: string
  ): Promise<Blob> {
    if (!zone || !itemPath) throw new Error('Zone and itemPath cannot be empty');
    const encodedZone = encodeURIComponent(zone);
    const encodedItemPath = this.encodePath(itemPath);
    const path = `data/${encodedZone}/raw/${encodedItemPath}`;

    return this.client._request<Blob>('GET', path, { responseType: 'blob' });
  }

  /**
   * Upload the raw blob content to an existing data item.
   * This is a lower-level method. Consider using uploadFile() for a complete upload workflow.
   * @param zone The data zone.
   * @param itemPath The path to the existing data item.
   * @param blob The Blob or Buffer containing the content to upload.
   * @param contentType The MIME type of the content (default: 'application/octet-stream').
   * @param options Additional request options.
   * @returns Promise resolving to upload result metadata.
   */
  async uploadBlob(
    zone: string,
    itemPath: string,
    body: Blob | any,
    contentType: string = 'application/octet-stream',
    options?: DataUploadOptions | Options  // Accept both types for backward compatibility
  ): Promise<{ key: string; contentHash: string; originalSize: number }> {
    if (!zone || !itemPath) throw new Error('Zone and itemPath cannot be empty');
    if (!body) throw new Error('Blob content cannot be empty');

    const encodedZone = encodeURIComponent(zone);
    const encodedItemPath = this.encodePath(itemPath);
    const path = `data/${encodedZone}/blob/${encodedItemPath}`;

    let actualBody: Blob;

    // Handle different input types
    if (typeof body === 'object' && body !== null && 
        typeof (body as any).constructor !== 'undefined' &&
        (body as any).constructor.name === 'Buffer') {
      // Handle Node.js Buffer (check by constructor name to avoid direct Buffer reference)
      actualBody = new Blob([body], { type: contentType });
    } else if (body instanceof Blob) {
      actualBody = body;
    } else if (typeof body === 'object' && body !== null && typeof body.pipe === 'function') {
      // Handle Node.js streams - simplified approach for browser compatibility
      throw new Error('Stream uploads are not supported in browser environment. Please convert to Blob first.');
    } else {
      throw new Error('Unsupported body type for blob upload. Expected Blob or Buffer.');
    }

    // Set up headers for blob upload
    const headers: { [key: string]: string } = {
      ...(options?.headers as { [key: string]: string }),
    };
    
    // Always set Content-Type for blob uploads to ensure backend receives it correctly
    headers['Content-Type'] = contentType;

    // Extract only valid request options, filtering out DataUploadOptions-specific fields
    const validOptions: Options = { ...options };
    if (validOptions && typeof validOptions === 'object') {
      delete (validOptions as any).contentType;
      delete (validOptions as any).overwrite;
    }
    
    const finalRequestOptions: Options = {
      ...validOptions,
      body: actualBody,
      headers: headers,
      skipContentTypeHeader: true, // Skip automatic JSON Content-Type
    };

    return this.client._request('PUT', path, finalRequestOptions);
  }

  /**
   * Store data in a zone with an optional itemPath using an "upsert" operation.
   * If itemPath is provided, the item will be created at that specific path.
   * If itemPath is null/undefined, a UUID will be auto-generated by the server.
   * @param zone The data zone.
   * @param itemPath The path for the data item (or null/undefined for auto-UUID).
   * @param data The data to store.
   * @param options Additional request options.
   * @returns Promise resolving to the created/updated item metadata.
   */
  async storeData(
    zone: string,
    itemPath: string | null | undefined,
    data: Partial<CreateDataItemDto>,
    options?: DataUploadOptions | Options  // Accept both types for backward compatibility
  ): Promise<{ key: string; uuid?: string }> {
    if (!zone) throw new Error('Zone cannot be empty');
    const encodedZone = encodeURIComponent(zone);
    const encodedItemPath = itemPath ? `/${this.encodePath(itemPath)}` : '';
    const path = `data/${encodedZone}${encodedItemPath}`;

    const requestOptions: Options = {
      ...options,
      body: data,
      params: {
        ...(options?.params || {}),
        overwrite: (options as any)?.overwrite ? 'true' : 'true', // Default to true for backward compatibility
      },
    };
    return this.client._request('POST', path, requestOptions);
  }

  /**
   * Create or update a data item. Supports JSON content or file uploads.
   * If itemPath is provided, it creates/updates at that path.
   * If itemPath is omitted or empty, the server may generate a UUID as the path.
   * @param zone The data zone.
   * @param itemPath Optional. The path for the data item. If omitted, server generates path (likely UUID).
   * @param data The data to create/update. Can include content, metadata, description, uuid, or a file.
   * @param options Request options.
   * @returns A promise resolving to the key and potentially UUID of the created/updated item.
   */
  async upsertData(
    zone: string,
    itemPath: string | null | undefined,
    data: Partial<CreateDataItemDto>,
    options?: Options
  ): Promise<{ key: string; uuid?: string }> {
    if (!zone) throw new Error('Zone cannot be empty');
    const encodedZone = encodeURIComponent(zone);
    const encodedItemPath = itemPath ? `/${this.encodePath(itemPath)}` : '';
    const path = `data/${encodedZone}${encodedItemPath}`;

    const requestOptions: Options = {
      ...options,
      body: data,
      params: {
        ...(options?.params || {}),
        overwrite: 'true',
      },
    };
    return this.client._request('POST', path, requestOptions);
  }

  /**
   * Store text content in a zone at the specified path.
   * @param zone The data zone.
   * @param itemPath The path for the text item.
   * @param textContent The text content to store.
   * @param contentType The MIME type of the content (default: 'text/plain').
   * @param metadata Optional metadata to associate with the item.
   * @param description Optional description of the item.
   * @param options Additional request options.
   * @returns Promise resolving to the key where the content was stored.
   */
  async storeText(
    zone: string,
    itemPath: string,
    textContent: string,
    contentType: string = 'text/plain',
    metadata?: Record<string, any>,
    description?: string,
    options?: DataUploadOptions | Options  // Accept both types for backward compatibility
  ): Promise<string> {
    const data: Partial<CreateDataItemDto> = {
      content: textContent,
      metadata: {
        ...metadata,
        contentType, // contentType goes in metadata, not as a top-level field
      },
      ...(description && { description }),
    };

    const result = await this.storeData(zone, itemPath, data, options);
    return result.key;
  }

  /**
   * Creates a new data item with an auto-generated UUID and stores the provided data.
   * @param zone The data zone.
   * @param data The data to store.
   * @param options Additional request options.
   * @returns Promise resolving to the created item metadata including the generated UUID.
   */
  async createWithUuid(
    zone: string,
    data: Partial<CreateDataItemDto>,
    options?: DataUploadOptions | Options  // Accept both types for backward compatibility
  ): Promise<{ key: string; uuid: string }> {
    if (!zone) throw new Error('Zone cannot be empty');
    
    // Use null itemPath to trigger UUID generation
    const result = await this.storeData(zone, null, data, options);
    
    if (!result.uuid) {
      throw new Error('Server did not return a UUID for the created item');
    }
    
    return {
      key: result.key,
      uuid: result.uuid
    };
  }

  /**
   * Store a new data item by UUID in the specified zone.
   * @param zone The data zone.
   * @param uuid The UUID for the item (or null to auto-generate).
   * @param data The data to store (should not include the uuid field).
   * @param options Additional request options.
   * @returns Promise resolving to the created item metadata.
   */
  async storeByUuid(
    zone: string,
    uuid: string | null | undefined,
    data: Omit<Partial<CreateDataItemDto>, 'uuid'>,
    options?: DataUploadOptions | Options  // Accept both types for backward compatibility
  ): Promise<{ key: string; uuid: string }> {
    if (!zone) throw new Error('Zone cannot be empty');
    
    // Use null itemPath for UUID-based storage, let the server handle UUID assignment
    const result = await this.storeData(zone, null, data, options);
    
    if (!result.uuid) {
      throw new Error('Server did not return a UUID for the created item');
    }
    
    return {
      key: result.key,
      uuid: result.uuid
    };
  }

  /**
   * Store JSON data in the data store.
   * @param zone The data zone.
   * @param itemPath The path for the data item.
   * @param jsonContent The JSON content to store.
   * @param metadata Optional metadata for the item.
   * @param description Optional description for the item.
   * @returns A promise resolving to the storage location.
   */
  async storeJson(
    zone: string,
    itemPath: string,
    jsonContent: any,
    metadata?: Record<string, any>,
    description?: string
  ): Promise<string> {
    const data: Partial<CreateDataItemDto> = {
      content: jsonContent,
      metadata: {
        ...metadata,
        contentType: 'application/json'
      },
      description
    };

    const result = await this.upsertData(zone, itemPath, data);
    return result.key;
  }

  /**
   * Create or update a data item with a specific UUID in a UUID zone.
   * This method makes it explicit that you can provide your own UUID.
   * @param zone The data zone (should be a UUID zone like 'secure-uuids').
   * @param uuid Optional. The UUID to use for the item. If not provided, server generates one.
   * @param data The data to create/update. Can include content, metadata, description.
   * @param options Request options.
   * @returns A promise resolving to the key and UUID of the created/updated item.
   */
  async upsertDataWithUuid(
    zone: string,
    uuid: string | null | undefined,
    data: Omit<Partial<CreateDataItemDto>, 'uuid'>,
    options?: DataUploadOptions
  ): Promise<{ key: string; uuid: string }> {
    if (!zone) throw new Error('Zone cannot be empty');
    
    const requestData: Partial<CreateDataItemDto> = {
      ...data,
      uuid: uuid || undefined // Include UUID if provided
    };

    const result = await this.upsertData(zone, '', requestData, options);
    
    if (!result.uuid) {
      throw new Error('Server did not return a UUID for the created item');
    }
    
    return {
      key: result.key,
      uuid: result.uuid
    };
  }

  /**
   * Store JSON data with a specific UUID in a UUID zone.
   * @param zone The data zone (should be a UUID zone).
   * @param uuid Optional. The UUID to use for the item. If not provided, server generates one.
   * @param jsonContent The JSON content to store.
   * @param metadata Optional metadata for the item.
   * @param description Optional description for the item.
   * @returns A promise resolving to the key and UUID of the stored item.
   */
  async storeJsonWithUuid(
    zone: string,
    uuid: string | null | undefined,
    jsonContent: any,
    metadata?: Record<string, any>,
    description?: string
  ): Promise<{ key: string; uuid: string }> {
    const data = {
      content: jsonContent,
      metadata: {
        ...metadata,
        contentType: 'application/json'
      },
      description
    };

    return this.upsertDataWithUuid(zone, uuid, data);
  }

  /**
   * Store text content with a specific UUID in a UUID zone.
   * @param zone The data zone (should be a UUID zone).
   * @param uuid Optional. The UUID to use for the item. If not provided, server generates one.
   * @param textContent The text content to store.
   * @param contentType Optional content type. Defaults to 'text/plain'.
   * @param metadata Optional metadata for the item.
   * @param description Optional description for the item.
   * @returns A promise resolving to the key and UUID of the stored item.
   */
  async storeTextWithUuid(
    zone: string,
    uuid: string | null | undefined,
    textContent: string,
    contentType: string = 'text/plain',
    metadata?: Record<string, any>,
    description?: string
  ): Promise<{ key: string; uuid: string }> {
    const data = {
      content: textContent,
      metadata: {
        ...metadata,
        contentType: contentType
      },
      description
    };

    return this.upsertDataWithUuid(zone, uuid, data);
  }

  /**
   * Update the metadata of a data item.
   * @param zone The data zone.
   * @param itemPath The path to the data item within the zone.
   * @param metadataUpdate The metadata fields to update.
   * @returns Promise resolving to true if successful.
   */
  async updateMetadata(
    zone: string,
    itemPath: string,
    metadataUpdate: UpdateDataItemDto
  ): Promise<boolean> {
    if (!zone || !itemPath) throw new Error('Zone and itemPath cannot be empty');
    const path = `data/${encodeURIComponent(zone)}/${this.encodePath(itemPath)}`;
    const result = await this.client._request('PATCH', path, { body: metadataUpdate });
    return !!result;
  }

  /**
   * Delete a data item.
   * @param zone The data zone.
   * @param itemPath The path to the data item within the zone.
   * @returns Promise resolving to true if successful.
   */
  async delete(
    zone: string,
    itemPath: string
  ): Promise<boolean> {
    if (!zone || !itemPath) throw new Error('Zone and itemPath cannot be empty');
    const path = `data/${encodeURIComponent(zone)}/${this.encodePath(itemPath)}`;
    const result = await this.client._request('DELETE', path);
    return result?.success === true;
  }
} 